/** @jsxImportSource @emotion/react */
import { useId, useState } from 'react';

import { groupActivityLogEntriesBySection, groupActivityLogEntriesByYear } from '@/Client/Lib';
import { ActivityLogTableRow } from './ActivityLogTableRow';
import { ActivityLogTableSectionHeader } from './ActivityLogTableSectionHeader';
import { type ActivityLogTableStyles, DEFAULT_ACTIVITY_LOG_TABLE_STYLES } from './activityLogTableStyles';
import type { ActivityLogTableColumn, ActivityLogTableEntry, ActivityLogTableSection } from './activityLogTableTypes';

const getEventCountLabel = (count: number) => `${count} event${count === 1 ? '' : 's'}`;

/**
 * An activity log as a table: a titled, counted list of events, newest first, split under the
 * view's section headings when it gives them, else banded by year once it spans more than one. The view supplies the entries and the columns to show them in; the
 * table supplies the layout, so every log reads the same.
 */
export const ActivityLogTable = <Entry extends ActivityLogTableEntry>({
  title,
  emptyLabel,
  entries,
  sections,
  columns,
  styles = DEFAULT_ACTIVITY_LOG_TABLE_STYLES,
}: {
  title: string;
  emptyLabel: string;
  /** Newest first. */
  entries: readonly Entry[];
  /** Headings that split the entries by when they happened; sections with no entries go unshown. */
  sections?: readonly ActivityLogTableSection[];
  columns: readonly ActivityLogTableColumn<Entry>[];
  styles?: ActivityLogTableStyles;
}) => {
  const titleId = useId();
  const [expandedEntryIds, setExpandedEntryIds] = useState<ReadonlySet<string>>(new Set());
  const toggleExpanded = (entryId: string) =>
    setExpandedEntryIds(current => {
      const next = new Set(current);
      if (!next.delete(entryId)) next.add(entryId);
      return next;
    });
  // each entry's row, then the rows of its collapsed events while it's expanded
  const getRows = (groupEntries: readonly Entry[]) =>
    groupEntries.flatMap(entry => [
      { entry, isChild: false },
      ...(entry.children && expandedEntryIds.has(entry.id) ? (entry.children as Entry[]).map(child => ({ entry: child, isChild: true })) : []),
    ]);
  const eventCount = entries.reduce((count, entry) => count + (entry.children?.length ?? 1), 0);
  // the timeline rail runs from row to row through any heading between them: it stops only above the first row with nothing over it, and below the last row
  const hasHeading = (group: { section?: unknown; yearBand?: number }) => !!group.section || group.yearBand !== undefined;
  const groups = sections
    ? groupActivityLogEntriesBySection(sections, entries).map(({ section, entries: sectionEntries }) => ({ key: section.id, section, entries: sectionEntries }))
    : groupActivityLogEntriesByYear(entries).map(({ year, entries: yearEntries }, _index, yearGroups) => ({
      key: String(year),
      yearBand: yearGroups.length > 1 ? year : undefined,
      entries: yearEntries,
    }));

  return (
    <section css={styles.root} data-testid={'activity-log-table'} aria-labelledby={titleId}>
      <div css={styles.header}>
        <h3 id={titleId} css={styles.title}>{title}</h3>
        {entries.length > 0 ? <span css={styles.count} data-testid={'activity-log-table-count'}>{getEventCountLabel(eventCount)}</span> : null}
      </div>
      {entries.length === 0 ? <div css={styles.empty}>{emptyLabel}</div> : (
        <div css={styles.frame}>
          <table css={styles.table} aria-labelledby={titleId}>
            <colgroup>
              {columns.map(column => <col key={column.key} css={column.colStyle?.(styles)} />)}
            </colgroup>
            <thead>
              <tr>
                {columns.map((column, columnIndex) => (
                  <th
                    key={column.key}
                    scope={'col'}
                    css={[
                      styles.columnHeading,
                      columnIndex === 0 && styles.firstColumnHeading,
                      columnIndex === columns.length - 1 && styles.lastColumnHeading,
                    ]}
                  >
                    {column.heading ?? <span css={styles.visuallyHidden}>{column.srHeading}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            {groups.map((group, groupIndex) => (
              <tbody key={group.key}>
                {'section' in group ? <ActivityLogTableSectionHeader section={group.section} columns={columns} styles={styles} /> : null}
                {'yearBand' in group && group.yearBand !== undefined
                  ? (
                    <tr>
                      <th colSpan={columns.length} scope={'rowgroup'} css={styles.yearBand} data-testid={'activity-log-table-year'}>{group.yearBand}</th>
                    </tr>
                  )
                  : null}
                {getRows(group.entries).map(({ entry, isChild }, rowIndex, rows) => (
                  <ActivityLogTableRow
                    key={entry.id}
                    entry={entry}
                    columns={columns}
                    isFirst={rowIndex === 0 && !hasHeading(group)}
                    isLast={rowIndex === rows.length - 1 && groupIndex === groups.length - 1}
                    expansion={!isChild && entry.children
                      ? { expanded: expandedEntryIds.has(entry.id), onToggle: () => toggleExpanded(entry.id) }
                      : undefined}
                    styles={styles}
                  />
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
};
